/**
 * PlotBuilderAdminPanel — Plot Builder inside Admin → Editing & Books.
 *
 * Lets admins extract and edit the plot for any project, then feed it
 * to the Walker (Deep Plot Audit checkbox) and Drift Correction.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, GitBranch, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PlotBuilderPanel } from '@/components/panels/editor/PlotBuilderDialog';
import { adminApi } from '@/lib/api';

export default function PlotBuilderAdminPanel() {
  const [projects, setProjects]     = useState([]);
  const [selected, setSelected]     = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError]   = useState(null);

  useEffect(() => {
    setLoadingList(true);
    setListError(null);
    // Use the admin endpoint so all users' projects appear, not just the admin's own.
    adminApi.listAllProjects()
      .then((data) => {
        const list = Array.isArray(data) ? data : (data?.projects || []);
        setProjects(list);
        if (list.length > 0) setSelected(list[0]);
      })
      .catch((e) => {
        setListError(e?.response?.data?.detail || e?.message || 'Could not load projects.');
      })
      .finally(() => setLoadingList(false));
  }, []);

  const handleSelect = useCallback((pid) => {
    const p = projects.find((x) => x.id === pid);
    if (p) setSelected(p);
  }, [projects]);

  return (
    <div className="space-y-4" data-testid="plot-builder-admin-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GitBranch className="h-4 w-4 text-primary" />
          <h2 className="text-base font-semibold">Plot Builder</h2>
        </div>
        <p className="text-[11.5px] text-muted-foreground">
          Extract, edit and persist the structured plot. Available to Pro &amp; Studio authors.
        </p>
      </div>

      {/* Project selector */}
      {loadingList ? (
        <div className="flex items-center gap-2 text-muted-foreground text-[12.5px]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading projects…
        </div>
      ) : listError ? (
        <div className="rounded-md bg-destructive/10 px-3 py-2 text-[12px] text-destructive" data-testid="plot-admin-error">
          {listError}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Project
          </label>
          <select
            className="rounded-md border border-border bg-background px-2 py-1.5 text-[12.5px] max-w-xs"
            value={selected?.id || ''}
            onChange={(e) => handleSelect(e.target.value)}
            data-testid="plot-admin-project-select"
          >
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title || p.id}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Panel */}
      {selected ? (
        <div className="rounded-lg border border-border bg-card">
          <PlotBuilderPanel key={selected.id} project={selected} inDialog={false} />
        </div>
      ) : (
        !loadingList && (
          <p className="text-[12.5px] text-muted-foreground">
            No projects found. Create a project first.
          </p>
        )
      )}
    </div>
  );
}
